import { useState } from "react";
import { Link } from "react-router";

import { useNotification } from "@refinedev/core";
import { CalendarDaysIcon, XIcon } from "lucide-react";

import { RemoteMultiSelect } from "@/components/fields/remote-multi-select";
import { DetailSection, EmptyHint } from "@/components/show";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useEventGroupRepository } from "@/pages/event-groups/hooks/use-event-group-repository";
import {
  type GroupEventOption,
  useGroupEventSearch,
} from "@/pages/event-groups/hooks/use-group-event-search";
import { formatStart } from "@/pages/event-groups/lib/format-start";
import {
  refusalMessage,
  refusalNamingEvent,
} from "@/pages/event-groups/lib/refusal-message";
import {
  type EventGroup,
  type GroupEvent,
  MAX_EVENTS_PER_GROUP,
} from "@/types/event-group";

const TINA_NOTE =
  "Tina made this group. Changing its events here does not change the festival in Tina.";

/** The picker shows each event with its date and venue, because events of one name are common. */
const optionLabel = (option: GroupEventOption) => ({
  id: option.id,
  name: [option.name, option.startDate, option.venueName]
    .filter(Boolean)
    .join(" · "),
});

/**
 * The events a group holds, and what changes them: a remove per event, after a confirm, and a
 * picker that adds several at once. Every change goes to the admin API, a Tina group's too, and
 * a refused call shows the backend's own sentence and changes nothing on the page.
 */
export function GroupMembersEditor({
  group,
  events,
  lockedReason,
}: {
  group: EventGroup;
  events: GroupEvent[];
  /** Why the events cannot be changed right now. While it is set, the picker and the removes are off. */
  lockedReason?: string;
}) {
  const locked = Boolean(lockedReason);
  const repo = useEventGroupRepository();
  const { search } = useGroupEventSearch();
  const { open } = useNotification();
  const [picked, setPicked] = useState<GroupEventOption[]>([]);
  // The picker keeps its own copy of the selection; a new key is how it forgets it.
  const [pickerKey, setPickerKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<GroupEvent | null>(null);

  const memberIds = new Set(events.map((event) => event.id));

  async function add() {
    const ids = picked.map((option) => option.id);
    if (events.length + ids.length > MAX_EVENTS_PER_GROUP) {
      setError(`A group holds at most ${MAX_EVENTS_PER_GROUP} events.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await repo.addEvents(group.id, ids);
      if (!result.ok) {
        setError(
          refusalNamingEvent(
            result.message,
            picked.map((option) => ({
              id: option.id,
              label: optionLabel(option).name,
            })),
          ),
        );
        return;
      }
      open?.({
        type: "success",
        message:
          ids.length === 1 ? "Added 1 event" : `Added ${ids.length} events`,
      });
      setPicked([]);
      setPickerKey((key) => key + 1);
    } catch (caught) {
      setError(refusalMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function remove(event: GroupEvent) {
    setBusy(true);
    setError(null);
    try {
      const result = await repo.removeEvents(group.id, [event.id]);
      if (result.ok) {
        open?.({ type: "success", message: `Removed ${event.name}` });
      } else {
        setError(result.message);
      }
    } catch (caught) {
      setError(refusalMessage(caught));
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  }

  return (
    <DetailSection
      title="Events"
      icon={<CalendarDaysIcon className="size-4" />}
      action={
        <Badge variant="secondary">
          {events.length} of {MAX_EVENTS_PER_GROUP}
        </Badge>
      }
    >
      <div className="flex flex-col gap-4">
        {group.externalKey && (
          <p className="text-sm text-muted-foreground">{TINA_NOTE}</p>
        )}
        {lockedReason && (
          <p className="text-sm text-muted-foreground">{lockedReason}</p>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <RemoteMultiSelect<GroupEventOption>
            key={pickerKey}
            label="Add events"
            parentClassName="min-w-0 flex-1"
            className="w-full"
            triggerType="change"
            showSelected
            value={picked}
            // The picker hands back its own array; a copy is what makes React see the change.
            onValueChanged={(options) => setPicked(options ? [...options] : [])}
            accessorFn={optionLabel}
            onSearch={async (query, setStatus) => {
              const hits = await search(query);
              setStatus("success");
              return hits.filter((hit) => !memberIds.has(hit.id));
            }}
            disabled={busy || locked}
          />
          <Button
            type="button"
            disabled={busy || locked || picked.length === 0}
            onClick={() => void add()}
          >
            Add
          </Button>
        </div>

        {events.length === 0 ? (
          <EmptyHint>No events in this group yet.</EmptyHint>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Starts</TableHead>
                <TableHead>Venue</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">
                    <Link
                      to={`/events/edit/${event.id}`}
                      className="hover:underline"
                    >
                      {event.name}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatStart(event.startDate, event.timezone)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {event.venue?.name ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${event.name}`}
                      disabled={busy || locked}
                      onClick={() => setRemoving(event)}
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog
        open={removing !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen && !busy) setRemoving(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove this event from the group?</DialogTitle>
            <DialogDescription>
              {removing?.name} stays as an event. It just no longer belongs to{" "}
              {group.name}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => setRemoving(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={busy}
              onClick={() => removing && void remove(removing)}
            >
              Remove from group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DetailSection>
  );
}
